import {
  type SdmLivePlayerErrorCode,
  type SdmLivePlayerState,
} from '@solinkcorp/service-display-monitor';
import { type PlayerStatus } from 'components/VideoPlayer/controller/PlaybackController';
import { isCameraNotAccessibleError } from 'src/core/video/cameraNotAccessibleError';

/**
 * Map the playback controller's player state to the state reported to the SDM live feed widget.
 *
 * An error stays reported until the feed plays again. Restarts and the HLS fallback pass through
 * `loading` and `waiting`, and reporting those as `connecting` would hide the error overlay while
 * the stream is still broken.
 *
 * @param previous The state reported last time.
 * @param playerState The controller's current state for the player.
 * @param isOnline Whether the browser reports a network connection.
 * @returns The state to report to the widget.
 */
export function toSdmLivePlayerState({
  previous,
  playerState,
  isOnline,
}: {
  previous: SdmLivePlayerState;
  playerState: PlayerStatus['state'] | undefined;
  isOnline: boolean;
}): SdmLivePlayerState {
  if (!isOnline || playerState === 'error') return 'error';
  if (playerState === 'playing') return 'playing';
  if (previous === 'error') return 'error';
  return 'connecting';
}

/**
 * Classify why the player reports `error`.
 *
 * A reported error stays sticky while the player recovers, so its code does too: the cause that
 * put the feed in error is still the best explanation until the feed plays again.
 *
 * @param state The state reported to the widget, from `toSdmLivePlayerState`.
 * @param previous The error code reported last time, if any.
 * @param playerState The controller's current state for the player.
 * @param playerError The error the player attached to its status, if any.
 * @param isOnline Whether the browser reports a network connection.
 * @returns The error code, or undefined when `state` is not `error`.
 */
export function toSdmLivePlayerErrorCode({
  state,
  previous,
  playerState,
  playerError,
  isOnline,
}: {
  state: SdmLivePlayerState;
  previous: SdmLivePlayerErrorCode | undefined;
  playerState: PlayerStatus['state'] | undefined;
  playerError: unknown;
  isOnline: boolean;
}): SdmLivePlayerErrorCode | undefined {
  if (state !== 'error') return;
  if (!isOnline) return 'networkOffline';
  if (playerState === 'error') {
    return isCameraNotAccessibleError(playerError) ? 'cameraNotAccessible' : 'playbackFailed';
  }
  return previous ?? 'playbackFailed';
}
